import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthShell } from './AcceptInvitation';
import { Check, AlertCircle, Eye, EyeOff } from 'lucide-react';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = password.length >= 12;
  const matches = password === confirm && confirm.length > 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', { token, newPassword: password });
      toast.success('Password reset', 'Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <div className="space-y-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <AlertCircle className="h-5 w-5" />
          </div>
          <h2 className="font-display text-2xl">Invalid reset link</h2>
          <p className="text-sm text-muted-foreground">This link is missing a token. Request a new one.</p>
          <Button asChild variant="outline"><a href="/forgot-password">Request new link</a></Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="animate-fade-in space-y-5">
        <div>
          <h2 className="font-display text-2xl">Set a new password</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            All your active sessions will be signed out.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">New password</Label>
          <div className="relative">
            <Input
              id="pw"
              type={showPassword ? 'text' : 'password'}
              required
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            {valid ? <Check className="h-3 w-3 text-emerald-600" /> : <span className="h-3 w-3 rounded-full border border-border" />}
            <span className={valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}>
              At least 12 characters
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cpw">Confirm password</Label>
          <Input id="cpw" type={showPassword ? 'text' : 'password'} required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {confirm && !matches && <p className="text-xs text-destructive">Passwords don&apos;t match</p>}
        </div>
        {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>}
        <Button type="submit" className="w-full" disabled={!valid || !matches || submitting}>
          {submitting ? 'Resetting...' : 'Reset password'}
        </Button>
      </form>
    </AuthShell>
  );
}
